import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i2$2 from '@angular/cdk/overlay';
import { CdkOverlayOrigin, OverlayModule } from '@angular/cdk/overlay';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Directive, Input, inject, TemplateRef, Component, ChangeDetectionStrategy, EventEmitter, HostBinding, Output, ChangeDetectorRef, HostListener, ViewChild, ElementRef, ContentChild, NgModule } from '@angular/core';
import { buildTrackByIdentity } from '@simpl/element-ng/common';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, takeUntil, tap, switchMap } from 'rxjs/operators';
import * as i2$1 from '@simpl/element-ng/auto-collapsable-list';
import { SiAutoCollapsableListModule } from '@simpl/element-ng/auto-collapsable-list';
import * as i2 from '@simpl/element-ng/translate';
import { SiTranslateModule } from '@simpl/element-ng/translate';
import * as i3 from '@simpl/element-ng/autocomplete';
import { SiAutocompleteDirective, SiAutocompleteModule } from '@simpl/element-ng/autocomplete';
import { CdkListbox, CdkOption } from '@angular/cdk/listbox';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

const SI_SELECT_OPTIONS_STRATEGY = new InjectionToken('si-select.options-strategy');

/**
 * Input options strategy base class, for eagerly fetched options.
 */
class SiSelectOptionsStrategyBase {
    rowsSubject$ = new BehaviorSubject([]);
    rows$ = this.rowsSubject$.asObservable();
    get rows() {
        return this.rowsSubject$.value;
    }
    allRowsSubject$ = new BehaviorSubject([]);
    allRows$ = this.allRowsSubject$.asObservable();
    get allRows() {
        return this.allRowsSubject$.value;
    }
    findOptionsForValues(values) {
        return this.allRows$.pipe(map(allRows => allRows
            .map(row => (row.type === 'group' ? row.options : row))
            .flat()
            .filter(option => values.some(value => this.optionsEqual(value, option.value)))));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectOptionsStrategyBase, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectOptionsStrategyBase, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectOptionsStrategyBase, decorators: [{
            type: Directive
        }] });

/**
 * The directive allows to pass custom options.
 * Otherwise, use the {@link SiSelectSimpleOptionsDirective} directive.
 *
 * @example
 * <si-select [complexOptions]="['v1', 'v2', 'v3']"></si-select>
 * <si-select [complexOptions]="{ g1: ['g1.i1', 'g1.i2'], g2: ['g2.i1']}"></si-select>
 */
class SiSelectComplexOptionsDirective extends SiSelectOptionsStrategyBase {
    /** Options to be shown in select dropdown. */
    complexOptions;
    /**
     * Customizable trackBy function for directive ngFor.
     */
    trackBy = buildTrackByIdentity();
    /**
     * By default, values are check on equality by reference. Override to customize the behavior.
     */
    // override the input be to better understandable by consumer
    // eslint-disable-next-line @angular-eslint/no-input-rename
    optionsEqual = (a, b) => a === b;
    /**
     * The valueProvider is used to extract the display text of a value.
     */
    valueProvider;
    /**
     * Provides Value for the display text of the dropdown group
     */
    groupProvider = () => undefined;
    /**
     * The disabledProvider is used to display menu items as disabled.
     */
    disabledProvider = () => false;
    ngOnChanges() {
        if (this.complexOptions instanceof Array) {
            this.allRowsSubject$.next(this.convertOptionsArray(this.complexOptions));
        }
        else if (this.complexOptions) {
            this.allRowsSubject$.next(Object.entries(this.complexOptions).map(([key, value]) => ({
                type: 'group',
                key,
                label: this.groupProvider(key) ?? key,
                options: this.convertOptionsArray(value)
            })));
        }
        this.updateOptionsSubject();
    }
    onFilter(filterValue) {
        this.updateOptionsSubject(filterValue);
    }
    convertOptionsArray(options) {
        return options.map(option => ({
            type: 'option',
            value: option,
            label: (this.valueProvider ? this.valueProvider(option) : undefined) ?? option + '',
            typeaheadLabel: this.valueProvider ? this.valueProvider(option) : undefined,
            disabled: this.disabledProvider(option)
        }));
    }
    updateOptionsSubject(filterValue) {
        if (filterValue) {
            const filterValueLC = filterValue.toLowerCase();
            const checkRow = (row) => (row.typeaheadLabel ?? row.label).toLowerCase().includes(filterValueLC);
            this.rowsSubject$.next(this.allRows.reduce((rows, row) => {
                if (row.type === 'option' && checkRow(row)) {
                    rows.push(row);
                }
                else if (row.type === 'group') {
                    if (row.label.toLowerCase().includes(filterValueLC)) {
                        rows.push(row);
                    }
                    else {
                        const options = row.options.filter(checkRow);
                        if (options.length) {
                            rows.push({ ...row, options });
                        }
                    }
                }
                return rows;
            }, []));
        }
        else {
            this.rowsSubject$.next(this.allRows);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectComplexOptionsDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectComplexOptionsDirective, isStandalone: true, selector: "si-select[complexOptions]", inputs: { complexOptions: "complexOptions", trackBy: "trackBy", optionsEqual: ["optionEqualCheckFn", "optionsEqual"], valueProvider: "valueProvider", groupProvider: "groupProvider", disabledProvider: "disabledProvider" }, providers: [
            { provide: SI_SELECT_OPTIONS_STRATEGY, useExisting: SiSelectComplexOptionsDirective }
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectComplexOptionsDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'si-select[complexOptions]',
                    providers: [
                        { provide: SI_SELECT_OPTIONS_STRATEGY, useExisting: SiSelectComplexOptionsDirective }
                    ],
                    standalone: true
                }]
        }], propDecorators: { complexOptions: [{
                type: Input
            }], trackBy: [{
                type: Input
            }], optionsEqual: [{
                type: Input,
                args: ['optionEqualCheckFn']
            }], valueProvider: [{
                type: Input
            }], groupProvider: [{
                type: Input
            }], disabledProvider: [{
                type: Input
            }] } });

/**
 * The directive allows to template/customize the value option rendering.
 * This requires to use the {@link SiSelectComplexOptionsDirective} to specify complex options as input.
 *
 * @example
 * <si-select [options]="[{ id: 'good', title: 'Good' }, { id: 'fair', title: 'Fair' }, { id: 'bad', title: 'Bad' }]" >
 *   <ng-template siSelectOptionTemplate let-option>{{ option.value | uppercase }}</ng-template>
 * </si-select>
 */
class SiSelectOptionTemplateDirective {
    // We need to have the templateRef here, so that the templates variables are properly typed
    templateRef = inject(TemplateRef);
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectOptionTemplateDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectOptionTemplateDirective, isStandalone: true, selector: "[siSelectOptionTemplate], [siSiSelectOptionTemplate]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectOptionTemplateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siSelectOptionTemplate], [siSiSelectOptionTemplate]',
                    standalone: true
                }]
        }] });

class SiSelectOptionComponent {
    option;
    optionTemplate;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectOptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectOptionComponent, isStandalone: true, selector: "si-select-option", inputs: { option: "option", optionTemplate: "optionTemplate" }, host: { classAttribute: "d-flex align-items-center overflow-hidden" }, ngImport: i0, template: "<ng-container\n  [ngTemplateOutlet]=\"optionTemplate ?? defaultOptionTemplate\"\n  [ngTemplateOutletContext]=\"{ $implicit: option }\"\n></ng-container>\n\n<ng-template siSelectOptionTemplate let-item #defaultOptionTemplate>\n  <i\n    *ngIf=\"item.icon\"\n    class=\"icon me-2\"\n    aria-hidden=\"true\"\n    [ngClass]=\"[item.icon, (!item.disabled && item.color) || '']\"\n  ></i>\n  <span *ngIf=\"item.label\" class=\"text-truncate\">{{ item.label | translate }}</span>\n</ng-template>\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "ngmodule", type: SiTranslateModule }, { kind: "pipe", type: i2.SiTranslatePipe, name: "translate" }, { kind: "directive", type: SiSelectOptionTemplateDirective, selector: "[siSelectOptionTemplate], [siSiSelectOptionTemplate]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectOptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-select-option', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [CommonModule, SiTranslateModule, SiSelectOptionTemplateDirective], host: {
                        class: 'd-flex align-items-center overflow-hidden'
                    }, template: "<ng-container\n  [ngTemplateOutlet]=\"optionTemplate ?? defaultOptionTemplate\"\n  [ngTemplateOutletContext]=\"{ $implicit: option }\"\n></ng-container>\n\n<ng-template siSelectOptionTemplate let-item #defaultOptionTemplate>\n  <i\n    *ngIf=\"item.icon\"\n    class=\"icon me-2\"\n    aria-hidden=\"true\"\n    [ngClass]=\"[item.icon, (!item.disabled && item.color) || '']\"\n  ></i>\n  <span *ngIf=\"item.label\" class=\"text-truncate\">{{ item.label | translate }}</span>\n</ng-template>\n" }]
        }], propDecorators: { option: [{
                type: Input,
                args: [{ required: true }]
            }], optionTemplate: [{
                type: Input
            }] } });

/**
 * Selection strategy base class.
 */
class SiSelectSelectionStrategy {
    _disabled = false;
    get disabled() {
        return this._disabled;
    }
    /**
     * Whether the select input is disabled.
     * @defaultref {@link _disabled}
     */
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this.stateChange$.next();
    }
    set value(value) {
        // Never call valueChange.emit on an input, which causes an ExpressionChangedAfterItHasBeenCheckedError
        if (this.inputValue !== value) {
            this.update(this.toArrayValue(value)).subscribe(() => {
                this.selectionChange$.next();
                this.onChange(this.inputValue);
            });
        }
    }
    /** Emitted when the selection is changed */
    valueChange = new EventEmitter();
    /**
     *  Emits when the selection changes.
     *  @internal
     */
    selectionChange$ = new Subject();
    /**
     * Emits when the state of the select control changes.
     * @internal
     */
    stateChange$ = new Subject();
    /**
     * Provides the internal value always as an array
     * @internal
     */
    get arrayValue() {
        return this.toArrayValue(this.inputValue);
    }
    /**
     * Registered form callback which shall be called on blur.
     * @internal
     */
    onTouched = () => { };
    onChange = () => { };
    selectOptions = inject(SI_SELECT_OPTIONS_STRATEGY);
    inputValue;
    newInputSubject = new Subject();
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    ngOnDestroy() {
        this.stateChange$.complete();
        this.selectionChange$.complete();
        this.newInputSubject.next();
        this.newInputSubject.complete();
    }
    /**
     * CDK Listbox value changed handler.
     * @internal
     *
     * @param value
     * @param triggerByUser
     */
    updateArrayValue(value) {
        this.update(value).subscribe(() => {
            this.onChange(this.inputValue);
            this.valueChange.emit(this.inputValue);
            this.selectionChange$.next();
        });
    }
    setDisabledState(isDisabled) {
        this._disabled = isDisabled;
        this.stateChange$.next();
    }
    writeValue(obj) {
        this.update(this.toArrayValue(obj)).subscribe(() => {
            this.valueChange.emit(this.inputValue);
            this.selectionChange$.next();
        });
    }
    update(values) {
        this.newInputSubject.next();
        return this.selectOptions.findOptionsForValues(values).pipe(takeUntil(this.newInputSubject), tap(options => {
            this.inputValue = this.fromArrayValue(options.map(option => option.value));
        }));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectSelectionStrategy, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectSelectionStrategy, inputs: { disabled: "disabled", value: "value" }, outputs: { valueChange: "valueChange" }, host: { properties: { "class.disabled": "this.disabled" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectSelectionStrategy, decorators: [{
            type: Directive
        }], propDecorators: { disabled: [{
                type: HostBinding,
                args: ['class.disabled']
            }, {
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

class SiSelectInputComponent {
    baseId;
    /** Aria labelledby of the select. */
    labelledby = null;
    /** Aria label of the select. */
    ariaLabel = null;
    open = false;
    controls;
    optionTemplate;
    readonly = false;
    openListbox = new EventEmitter();
    selectionStrategy = inject((SiSelectSelectionStrategy));
    selectOptions = inject(SI_SELECT_OPTIONS_STRATEGY);
    changeDetector = inject(ChangeDetectorRef);
    destroyer = new Subject();
    selectedRows = [];
    trackByIdentity = buildTrackByIdentity();
    constructor() {
        this.selectionStrategy.selectionChange$
            .pipe(takeUntil(this.destroyer), switchMap(() => this.selectOptions.findOptionsForValues(this.selectionStrategy.arrayValue)))
            .subscribe(options => {
            this.selectedRows = options;
            this.changeDetector.markForCheck();
        });
        this.selectionStrategy.stateChange$
            .pipe(takeUntil(this.destroyer))
            .subscribe(() => this.changeDetector.markForCheck());
    }
    ngOnDestroy() {
        this.destroyer.next();
        this.destroyer.complete();
    }
    blur() {
        if (!this.open) {
            this.selectionStrategy.onTouched();
        }
    }
    get tabindex() {
        return this.selectionStrategy.disabled ? '-1' : '0';
    }
    get hostLabelledby() {
        return `${this.baseId}-aria-label ${this.labelledby} ${this.baseId}-content`;
    }
    get disabled() {
        return this.selectionStrategy.disabled;
    }
    click() {
        this.openListbox.emit();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectInputComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectInputComponent, isStandalone: true, selector: "si-select-input", inputs: { baseId: "baseId", labelledby: "labelledby", ariaLabel: "ariaLabel", open: "open", controls: "controls", optionTemplate: "optionTemplate", readonly: "readonly" }, outputs: { openListbox: "openListbox" }, host: { attributes: { "role": "combobox", "aria-autocomplete": "none", "aria-haspopup": "listbox" }, listeners: { "blur": "blur()", "click": "click()", "keydown.arrowDown": "click()", "keydown.arrowUp": "click()", "keydown.enter": "click()", "keydown.space": "click()" }, properties: { "class.active": "this.open", "attr.aria-expanded": "this.open", "attr.aria-controls": "this.controls", "tabindex": "this.tabindex", "attr.aria-labelledby": "this.hostLabelledby", "attr.aria-disabled": "this.disabled", "class.disabled": "this.disabled" }, classAttribute: "select focus-none dropdown-toggle d-flex align-items-center ps-4 pe-5" }, ngImport: i0, template: "<span *ngIf=\"ariaLabel\" class=\"visually-hidden\" [id]=\"baseId + '-aria-label'\">{{ ariaLabel }}</span>\n<div\n  class=\"d-flex align-items-center flex-grow-1\"\n  [class.overflow-hidden]=\"!selectionStrategy.allowMultiple\"\n  [id]=\"baseId + '-content'\"\n  [siAutoCollapsableList]=\"selectionStrategy.allowMultiple\"\n>\n  <ng-container\n    *ngFor=\"let activeOption of selectedRows; let first = first; trackBy: trackByIdentity\"\n  >\n    <si-select-option\n      *ngIf=\"activeOption\"\n      siAutoCollapsableListItem\n      [option]=\"activeOption\"\n      [optionTemplate]=\"optionTemplate\"\n      [class.overflow-hidden]=\"!selectionStrategy.allowMultiple\"\n    ></si-select-option>\n  </ng-container>\n  <div\n    *ngIf=\"selectionStrategy.allowMultiple\"\n    #overflowItem=\"siAutoCollapsableListOverflowItem\"\n    siAutoCollapsableListOverflowItem\n  >\n    <div class=\"overflow-item\"> {{ overflowItem.hiddenItemCount }}+</div>\n  </div>\n</div>\n<i\n  class=\"dropdown-caret icon flip-rtl element-down-2\"\n  aria-hidden=\"true\"\n  [class.text-muted]=\"readonly\"\n></i>\n", styles: ["@charset \"UTF-8\";:host{font-weight:700;min-inline-size:72px;block-size:100%;color:var(--element-text-primary);padding-block:4px}:host.disabled{color:var(--element-text-disabled)}:host-context(.form-control) :host{font-weight:400;padding-block:3px}:host-context(.dropdown:not(.form-control)) :host:not(.disabled):hover{background:var(--element-base-1-hover)}:host-context(.dropdown:not(.form-control)) :host:not(.disabled).active{background:var(--element-base-1-selected)}si-select-option+si-select-option:before{content:\",\\a0\"}.overflow-item{border-radius:var(--element-radius-3);background:var(--element-base-0);margin-inline-start:4px;padding-inline:8px}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: SiAutoCollapsableListModule }, { kind: "directive", type: i2$1.SiAutoCollapsableListDirective, selector: "[siAutoCollapsableList]", inputs: ["siAutoCollapsableList"], exportAs: ["siAutoCollapsableList"] }, { kind: "directive", type: i2$1.SiAutoCollapsableListItemDirective, selector: "[siAutoCollapsableListItem]", inputs: ["forceHide"], exportAs: ["siAutoCollapsableListItem"] }, { kind: "directive", type: i2$1.SiAutoCollapsableListOverflowItemDirective, selector: "[siAutoCollapsableListOverflowItem]", exportAs: ["siAutoCollapsableListOverflowItem"] }, { kind: "component", type: SiSelectOptionComponent, selector: "si-select-option", inputs: ["option", "optionTemplate"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-select-input', standalone: true, imports: [CommonModule, SiAutoCollapsableListModule, SiSelectOptionComponent], changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        role: 'combobox',
                        class: 'select focus-none dropdown-toggle d-flex align-items-center ps-4 pe-5',
                        'aria-autocomplete': 'none',
                        'aria-haspopup': 'listbox'
                    }, template: "<span *ngIf=\"ariaLabel\" class=\"visually-hidden\" [id]=\"baseId + '-aria-label'\">{{ ariaLabel }}</span>\n<div\n  class=\"d-flex align-items-center flex-grow-1\"\n  [class.overflow-hidden]=\"!selectionStrategy.allowMultiple\"\n  [id]=\"baseId + '-content'\"\n  [siAutoCollapsableList]=\"selectionStrategy.allowMultiple\"\n>\n  <ng-container\n    *ngFor=\"let activeOption of selectedRows; let first = first; trackBy: trackByIdentity\"\n  >\n    <si-select-option\n      *ngIf=\"activeOption\"\n      siAutoCollapsableListItem\n      [option]=\"activeOption\"\n      [optionTemplate]=\"optionTemplate\"\n      [class.overflow-hidden]=\"!selectionStrategy.allowMultiple\"\n    ></si-select-option>\n  </ng-container>\n  <div\n    *ngIf=\"selectionStrategy.allowMultiple\"\n    #overflowItem=\"siAutoCollapsableListOverflowItem\"\n    siAutoCollapsableListOverflowItem\n  >\n    <div class=\"overflow-item\"> {{ overflowItem.hiddenItemCount }}+</div>\n  </div>\n</div>\n<i\n  class=\"dropdown-caret icon flip-rtl element-down-2\"\n  aria-hidden=\"true\"\n  [class.text-muted]=\"readonly\"\n></i>\n", styles: ["@charset \"UTF-8\";:host{font-weight:700;min-inline-size:72px;block-size:100%;color:var(--element-text-primary);padding-block:4px}:host.disabled{color:var(--element-text-disabled)}:host-context(.form-control) :host{font-weight:400;padding-block:3px}:host-context(.dropdown:not(.form-control)) :host:not(.disabled):hover{background:var(--element-base-1-hover)}:host-context(.dropdown:not(.form-control)) :host:not(.disabled).active{background:var(--element-base-1-selected)}si-select-option+si-select-option:before{content:\",\\a0\"}.overflow-item{border-radius:var(--element-radius-3);background:var(--element-base-0);margin-inline-start:4px;padding-inline:8px}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { baseId: [{
                type: Input,
                args: [{ required: true }]
            }], labelledby: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], open: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.active']
            }, {
                type: HostBinding,
                args: ['attr.aria-expanded']
            }], controls: [{
                type: Input,
                args: [{ required: true }]
            }, {
                type: HostBinding,
                args: ['attr.aria-controls']
            }], optionTemplate: [{
                type: Input
            }], readonly: [{
                type: Input
            }], openListbox: [{
                type: Output
            }], blur: [{
                type: HostListener,
                args: ['blur']
            }], tabindex: [{
                type: HostBinding,
                args: ['tabindex']
            }], hostLabelledby: [{
                type: HostBinding,
                args: ['attr.aria-labelledby']
            }], disabled: [{
                type: HostBinding,
                args: ['attr.aria-disabled']
            }, {
                type: HostBinding,
                args: ['class.disabled']
            }], click: [{
                type: HostListener,
                args: ['click']
            }, {
                type: HostListener,
                args: ['keydown.arrowDown']
            }, {
                type: HostListener,
                args: ['keydown.arrowUp']
            }, {
                type: HostListener,
                args: ['keydown.enter']
            }, {
                type: HostListener,
                args: ['keydown.space']
            }] } });

class SiSelectOptionRowComponent {
    option;
    optionTemplate;
    selected = false;
    get dataId() {
        return this.option.value;
    }
    get disabled() {
        return !!this.option.disabled;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectOptionRowComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectOptionRowComponent, isStandalone: true, selector: "si-select-option-row", inputs: { option: "option", optionTemplate: "optionTemplate", selected: "selected" }, host: { properties: { "attr.data-id": "this.dataId", "class.disabled": "this.disabled" }, classAttribute: "dropdown-item focus-inside pe-4" }, ngImport: i0, template: "<si-select-option [option]=\"option\" [optionTemplate]=\"optionTemplate\"></si-select-option>\n<span *ngIf=\"selected\" class=\"ms-auto selected-marker\" aria-hidden=\"true\">\n  <span class=\"element-ok\"></span>\n</span>\n", styles: [".dropdown-item.active:focus{background-color:var(--element-base-1-selected);box-shadow:none!important}.selected-marker>span{font-size:1.5rem;margin-inline-end:-.25rem}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiSelectOptionComponent, selector: "si-select-option", inputs: ["option", "optionTemplate"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectOptionRowComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-select-option-row', standalone: true, imports: [CommonModule, SiSelectOptionComponent], host: {
                        class: 'dropdown-item focus-inside pe-4'
                    }, changeDetection: ChangeDetectionStrategy.OnPush, template: "<si-select-option [option]=\"option\" [optionTemplate]=\"optionTemplate\"></si-select-option>\n<span *ngIf=\"selected\" class=\"ms-auto selected-marker\" aria-hidden=\"true\">\n  <span class=\"element-ok\"></span>\n</span>\n", styles: [".dropdown-item.active:focus{background-color:var(--element-base-1-selected);box-shadow:none!important}.selected-marker>span{font-size:1.5rem;margin-inline-end:-.25rem}\n"] }]
        }], propDecorators: { option: [{
                type: Input,
                args: [{ required: true }]
            }], optionTemplate: [{
                type: Input
            }], selected: [{
                type: Input
            }], dataId: [{
                type: HostBinding,
                args: ['attr.data-id']
            }], disabled: [{
                type: HostBinding,
                args: ['class.disabled']
            }] } });

/**
 * The directive allows to template/customize the group option rendering.
 * This requires to use the {@link SiSelectComplexOptionsDirective} to specify complex options as input.
 *
 * @example
 * <si-select [complexOptions]="{ g1: ['g1.i1', 'g1.i2'], g2: ['g2.i1'] }" >
 *   <ng-template siSelectGroupTemplate let-group>{{ group.key | uppercase }}</ng-template>
 * </si-select>
 */
class SiSelectGroupTemplateDirective {
    // We need to have the templateRef here, so that the templates variables are properly typed
    templateRef = inject(TemplateRef);
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectGroupTemplateDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectGroupTemplateDirective, isStandalone: true, selector: "[siSelectGroupTemplate], [siSiSelectGroupTemplate]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectGroupTemplateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siSelectGroupTemplate], [siSiSelectGroupTemplate]',
                    standalone: true
                }]
        }] });

class SiSelectOptionRowTemplateDirective {
    templateRef = inject(TemplateRef);
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectOptionRowTemplateDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectOptionRowTemplateDirective, isStandalone: true, selector: "[siSelectOptionRowTemplate]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectOptionRowTemplateDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siSelectOptionRowTemplate]',
                    standalone: true
                }]
        }] });

class SiSelectListBase {
    baseId;
    optionTemplate;
    groupTemplate;
    labelledby = null;
    closeOverlay = new EventEmitter();
    rows = [];
    selectionStrategy = inject((SiSelectSelectionStrategy));
    selectOptions = inject(SI_SELECT_OPTIONS_STRATEGY);
    destroyer = new Subject();
    constructor() {
        this.selectOptions.rows$
            .pipe(takeUntil(this.destroyer))
            .subscribe(options => (this.rows = options));
    }
    ngOnDestroy() {
        this.destroyer.next();
        this.destroyer.complete();
    }
    trackByItem = (index, item) => item;
    closeOverlayAlways() {
        this.closeOverlay.emit();
    }
    closeOverlayIfSingle() {
        if (!this.selectionStrategy.allowMultiple) {
            this.closeOverlayAlways();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectListBase, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectListBase, inputs: { baseId: "baseId", optionTemplate: "optionTemplate", groupTemplate: "groupTemplate", labelledby: "labelledby" }, outputs: { closeOverlay: "closeOverlay" }, host: { classAttribute: "dropdown-menu show position-static overflow-auto w-100 py-4" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectListBase, decorators: [{
            type: Directive,
            args: [{
                    host: {
                        class: 'dropdown-menu show position-static overflow-auto w-100 py-4'
                    }
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { baseId: [{
                type: Input,
                args: [{ required: true }]
            }], optionTemplate: [{
                type: Input
            }], groupTemplate: [{
                type: Input
            }], labelledby: [{
                type: Input
            }], closeOverlay: [{
                type: Output
            }] } });

class SiSelectListHasFilterComponent extends SiSelectListBase {
    filterPlaceholder;
    noResultsFoundLabel;
    filterInput;
    get id() {
        return `${this.baseId}-listbox`;
    }
    constructor() {
        super();
        if (!this.selectOptions.onFilter) {
            console.error('Missing implementation for `onFilter`');
        }
    }
    ngOnInit() {
        this.selectOptions.onFilter();
        setTimeout(() => this.filterInput.nativeElement.focus());
    }
    input() {
        this.selectOptions.onFilter(this.filterInput.nativeElement.value);
    }
    select(newValue) {
        if (this.selectionStrategy.allowMultiple) {
            if (this.selectionStrategy.arrayValue.includes(newValue)) {
                this.selectionStrategy.updateArrayValue(this.selectionStrategy.arrayValue.filter(value => value !== newValue));
            }
            else {
                this.selectionStrategy.updateArrayValue([...this.selectionStrategy.arrayValue, newValue]);
            }
        }
        else {
            this.selectionStrategy.updateArrayValue([newValue]);
        }
        this.closeOverlayIfSingle();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectListHasFilterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectListHasFilterComponent, isStandalone: true, selector: "si-select-list-has-filter", inputs: { filterPlaceholder: "filterPlaceholder", noResultsFoundLabel: "noResultsFoundLabel" }, host: { properties: { "id": "this.id" }, classAttribute: "pt-0" }, viewQueries: [{ propertyName: "filterInput", first: true, predicate: ["filterInput"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"pt-5 pb-5 px-5 position-sticky top-0 bg-base-1\">\n  <div class=\"position-relative\">\n    <input\n      #filterInput\n      #siAutocomplete=\"siAutocomplete\"\n      siAutocomplete\n      class=\"form-control ps-9 border-0 rounded-2 bg-base-0\"\n      [placeholder]=\"filterPlaceholder | translate\"\n      [id]=\"baseId + 'filter-input'\"\n      [attr.aria-labelledby]=\"baseId + '-aria-label' + ' ' + labelledby\"\n      (input)=\"input()\"\n      (keydown.tab)=\"closeOverlayAlways()\"\n    />\n    <span\n      class=\"element-search icon text-secondary position-absolute top-0 bottom-0 d-flex align-items-center ps-2\"\n    ></span>\n  </div>\n</div>\n<div\n  [siAutocompleteListboxFor]=\"siAutocomplete\"\n  [attr.aria-labelledby]=\"baseId + '-aria-label' + ' ' + labelledby\"\n  (siAutocompleteOptionSubmitted)=\"select($event)\"\n>\n  <div\n    *ngIf=\"!rows.length\"\n    role=\"option\"\n    aria-disabled=\"true\"\n    aria-selected=\"false\"\n    class=\"dropdown-item disabled text-secondary justify-content-center mb-2\"\n  >\n    <span>{{ noResultsFoundLabel | translate }}</span>\n  </div>\n  <ng-container *ngFor=\"let item of rows; let index = index; trackBy: trackByItem\">\n    <div\n      *ngIf=\"item.type === 'group'\"\n      role=\"group\"\n      class=\"ps-0\"\n      aria-disabled=\"false\"\n      [attr.aria-labelledby]=\"baseId + '-' + index + '-group-header'\"\n    >\n      <div\n        class=\"dropdown-header\"\n        role=\"presentation\"\n        [id]=\"baseId + '-' + index + '-group-header'\"\n      >\n        <ng-container\n          [ngTemplateOutlet]=\"groupTemplate ?? defaultGroupTemplate\"\n          [ngTemplateOutletContext]=\"{ $implicit: item }\"\n        ></ng-container>\n      </div>\n      <ng-container\n        *ngFor=\"let optionRow of item.options; trackBy: trackByItem\"\n        [ngTemplateOutlet]=\"optionRowTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: optionRow }\"\n      ></ng-container>\n    </div>\n    <ng-container\n      *ngIf=\"item.type === 'option'\"\n      [ngTemplateOutlet]=\"optionRowTemplate\"\n      [ngTemplateOutletContext]=\"{ $implicit: item }\"\n    ></ng-container>\n  </ng-container>\n\n  <ng-template siSelectOptionRowTemplate let-option #optionRowTemplate>\n    <si-select-option-row\n      [option]=\"option\"\n      [optionTemplate]=\"optionTemplate\"\n      [siAutocompleteOption]=\"option.value\"\n      [disabled]=\"option.disabled\"\n      [selected]=\"selectionStrategy.arrayValue.includes(option.value)\"\n    ></si-select-option-row>\n  </ng-template>\n</div>\n<ng-template siSelectGroupTemplate let-group #defaultGroupTemplate>\n  {{ group.label | translate }}\n</ng-template>\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: SiAutocompleteDirective, selector: "input[siAutocomplete]", exportAs: ["siAutocomplete"] }, { kind: "directive", type: SiSelectGroupTemplateDirective, selector: "[siSelectGroupTemplate], [siSiSelectGroupTemplate]" }, { kind: "component", type: SiSelectOptionRowComponent, selector: "si-select-option-row", inputs: ["option", "optionTemplate", "selected"] }, { kind: "directive", type: SiSelectOptionRowTemplateDirective, selector: "[siSelectOptionRowTemplate]" }, { kind: "ngmodule", type: SiTranslateModule }, { kind: "pipe", type: i2.SiTranslatePipe, name: "translate" }, { kind: "ngmodule", type: SiAutocompleteModule }, { kind: "directive", type: i3.SiAutocompleteListboxDirective, selector: "[siAutocompleteListboxFor]", inputs: ["id", "siAutocompleteListboxFor", "siAutocompleteDefaultIndex"], outputs: ["siAutocompleteOptionSubmitted"], exportAs: ["siAutocompleteListbox"] }, { kind: "directive", type: i3.SiAutocompleteOptionDirective, selector: "[siAutocompleteOption]", inputs: ["id", "disabled", "siAutocompleteOption"], exportAs: ["siAutocompleteOption"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectListHasFilterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-select-list-has-filter', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [
                        CommonModule,
                        SiAutocompleteDirective,
                        SiSelectGroupTemplateDirective,
                        SiSelectOptionRowComponent,
                        SiSelectOptionRowTemplateDirective,
                        SiTranslateModule,
                        SiAutocompleteModule
                    ], host: { class: 'pt-0' }, template: "<div class=\"pt-5 pb-5 px-5 position-sticky top-0 bg-base-1\">\n  <div class=\"position-relative\">\n    <input\n      #filterInput\n      #siAutocomplete=\"siAutocomplete\"\n      siAutocomplete\n      class=\"form-control ps-9 border-0 rounded-2 bg-base-0\"\n      [placeholder]=\"filterPlaceholder | translate\"\n      [id]=\"baseId + 'filter-input'\"\n      [attr.aria-labelledby]=\"baseId + '-aria-label' + ' ' + labelledby\"\n      (input)=\"input()\"\n      (keydown.tab)=\"closeOverlayAlways()\"\n    />\n    <span\n      class=\"element-search icon text-secondary position-absolute top-0 bottom-0 d-flex align-items-center ps-2\"\n    ></span>\n  </div>\n</div>\n<div\n  [siAutocompleteListboxFor]=\"siAutocomplete\"\n  [attr.aria-labelledby]=\"baseId + '-aria-label' + ' ' + labelledby\"\n  (siAutocompleteOptionSubmitted)=\"select($event)\"\n>\n  <div\n    *ngIf=\"!rows.length\"\n    role=\"option\"\n    aria-disabled=\"true\"\n    aria-selected=\"false\"\n    class=\"dropdown-item disabled text-secondary justify-content-center mb-2\"\n  >\n    <span>{{ noResultsFoundLabel | translate }}</span>\n  </div>\n  <ng-container *ngFor=\"let item of rows; let index = index; trackBy: trackByItem\">\n    <div\n      *ngIf=\"item.type === 'group'\"\n      role=\"group\"\n      class=\"ps-0\"\n      aria-disabled=\"false\"\n      [attr.aria-labelledby]=\"baseId + '-' + index + '-group-header'\"\n    >\n      <div\n        class=\"dropdown-header\"\n        role=\"presentation\"\n        [id]=\"baseId + '-' + index + '-group-header'\"\n      >\n        <ng-container\n          [ngTemplateOutlet]=\"groupTemplate ?? defaultGroupTemplate\"\n          [ngTemplateOutletContext]=\"{ $implicit: item }\"\n        ></ng-container>\n      </div>\n      <ng-container\n        *ngFor=\"let optionRow of item.options; trackBy: trackByItem\"\n        [ngTemplateOutlet]=\"optionRowTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: optionRow }\"\n      ></ng-container>\n    </div>\n    <ng-container\n      *ngIf=\"item.type === 'option'\"\n      [ngTemplateOutlet]=\"optionRowTemplate\"\n      [ngTemplateOutletContext]=\"{ $implicit: item }\"\n    ></ng-container>\n  </ng-container>\n\n  <ng-template siSelectOptionRowTemplate let-option #optionRowTemplate>\n    <si-select-option-row\n      [option]=\"option\"\n      [optionTemplate]=\"optionTemplate\"\n      [siAutocompleteOption]=\"option.value\"\n      [disabled]=\"option.disabled\"\n      [selected]=\"selectionStrategy.arrayValue.includes(option.value)\"\n    ></si-select-option-row>\n  </ng-template>\n</div>\n<ng-template siSelectGroupTemplate let-group #defaultGroupTemplate>\n  {{ group.label | translate }}\n</ng-template>\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { filterPlaceholder: [{
                type: Input,
                args: [{ required: true }]
            }], noResultsFoundLabel: [{
                type: Input,
                args: [{ required: true }]
            }], filterInput: [{
                type: ViewChild,
                args: ['filterInput']
            }], id: [{
                type: HostBinding,
                args: ['id']
            }] } });

class SiSelectListComponent extends SiSelectListBase {
    listbox;
    ngOnInit() {
        setTimeout(() => this.listbox.nativeElement.focus());
    }
    listBoxValueChange(changeEvent) {
        this.selectionStrategy.updateArrayValue(changeEvent.value.slice());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectListComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectListComponent, isStandalone: true, selector: "si-select-list", viewQueries: [{ propertyName: "listbox", first: true, predicate: CdkListbox, descendants: true, read: ElementRef }], usesInheritance: true, ngImport: i0, template: "<div\n  cdkListbox\n  cdkListboxUseActiveDescendant\n  class=\"focus-none\"\n  [id]=\"baseId + '-listbox'\"\n  [cdkListboxMultiple]=\"selectionStrategy.allowMultiple\"\n  [cdkListboxValue]=\"selectionStrategy.arrayValue\"\n  [attr.aria-labelledby]=\"baseId + '-aria-label' + ' ' + labelledby + ' ' + (baseId + '-content')\"\n  (cdkListboxValueChange)=\"listBoxValueChange($event)\"\n  (keydown.tab)=\"closeOverlayAlways()\"\n  (click)=\"closeOverlayIfSingle()\"\n  (keydown.enter)=\"closeOverlayIfSingle()\"\n  (keydown.space)=\"closeOverlayIfSingle()\"\n>\n  <ng-container *ngFor=\"let item of rows; let index = index; trackBy: trackByItem\">\n    <div\n      *ngIf=\"item.type === 'group'\"\n      role=\"group\"\n      class=\"ps-0\"\n      aria-disabled=\"false\"\n      [attr.aria-labelledby]=\"baseId + '-' + index + '-group-header'\"\n    >\n      <span\n        class=\"dropdown-header\"\n        role=\"presentation\"\n        [id]=\"baseId + '-' + index + '-group-header'\"\n      >\n        <ng-container\n          [ngTemplateOutlet]=\"groupTemplate ?? defaultGroupTemplate\"\n          [ngTemplateOutletContext]=\"{ $implicit: item }\"\n        ></ng-container>\n      </span>\n      <ng-container\n        *ngFor=\"let optionRow of item.options; trackBy: trackByItem\"\n        [ngTemplateOutlet]=\"optionRowTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: optionRow }\"\n      ></ng-container>\n    </div>\n    <ng-container\n      *ngIf=\"item.type === 'option'\"\n      [ngTemplateOutlet]=\"optionRowTemplate\"\n      [ngTemplateOutletContext]=\"{ $implicit: item }\"\n    ></ng-container>\n  </ng-container>\n  <ng-template siSelectOptionRowTemplate let-option #optionRowTemplate>\n    <si-select-option-row\n      #cdkOption=\"cdkOption\"\n      class=\"dropdown-item focus-inside pe-4\"\n      [option]=\"option\"\n      [optionTemplate]=\"optionTemplate\"\n      [cdkOption]=\"option.value\"\n      [cdkOptionDisabled]=\"option.disabled\"\n      [cdkOptionTypeaheadLabel]=\"option.typeaheadLabel! | translate\"\n      [class.active]=\"cdkOption.isActive()\"\n      [selected]=\"cdkOption.isSelected()\"\n    ></si-select-option-row>\n  </ng-template>\n</div>\n\n<ng-template siSelectGroupTemplate let-group #defaultGroupTemplate>\n  {{ group.label | translate }}\n</ng-template>\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: CdkListbox, selector: "[cdkListbox]", inputs: ["id", "tabindex", "cdkListboxValue", "cdkListboxMultiple", "cdkListboxDisabled", "cdkListboxUseActiveDescendant", "cdkListboxOrientation", "cdkListboxCompareWith", "cdkListboxNavigationWrapDisabled", "cdkListboxNavigatesDisabledOptions"], outputs: ["cdkListboxValueChange"], exportAs: ["cdkListbox"] }, { kind: "ngmodule", type: SiTranslateModule }, { kind: "pipe", type: i2.SiTranslatePipe, name: "translate" }, { kind: "directive", type: CdkOption, selector: "[cdkOption]", inputs: ["id", "cdkOption", "cdkOptionTypeaheadLabel", "cdkOptionDisabled", "tabindex"], exportAs: ["cdkOption"] }, { kind: "directive", type: SiSelectOptionRowTemplateDirective, selector: "[siSelectOptionRowTemplate]" }, { kind: "directive", type: SiSelectGroupTemplateDirective, selector: "[siSelectGroupTemplate], [siSiSelectGroupTemplate]" }, { kind: "component", type: SiSelectOptionRowComponent, selector: "si-select-option-row", inputs: ["option", "optionTemplate", "selected"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-select-list', standalone: true, imports: [
                        CommonModule,
                        CdkListbox,
                        SiSelectOptionComponent,
                        SiTranslateModule,
                        CdkOption,
                        SiSelectOptionRowTemplateDirective,
                        SiSelectGroupTemplateDirective,
                        SiSelectOptionRowComponent
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n  cdkListbox\n  cdkListboxUseActiveDescendant\n  class=\"focus-none\"\n  [id]=\"baseId + '-listbox'\"\n  [cdkListboxMultiple]=\"selectionStrategy.allowMultiple\"\n  [cdkListboxValue]=\"selectionStrategy.arrayValue\"\n  [attr.aria-labelledby]=\"baseId + '-aria-label' + ' ' + labelledby + ' ' + (baseId + '-content')\"\n  (cdkListboxValueChange)=\"listBoxValueChange($event)\"\n  (keydown.tab)=\"closeOverlayAlways()\"\n  (click)=\"closeOverlayIfSingle()\"\n  (keydown.enter)=\"closeOverlayIfSingle()\"\n  (keydown.space)=\"closeOverlayIfSingle()\"\n>\n  <ng-container *ngFor=\"let item of rows; let index = index; trackBy: trackByItem\">\n    <div\n      *ngIf=\"item.type === 'group'\"\n      role=\"group\"\n      class=\"ps-0\"\n      aria-disabled=\"false\"\n      [attr.aria-labelledby]=\"baseId + '-' + index + '-group-header'\"\n    >\n      <span\n        class=\"dropdown-header\"\n        role=\"presentation\"\n        [id]=\"baseId + '-' + index + '-group-header'\"\n      >\n        <ng-container\n          [ngTemplateOutlet]=\"groupTemplate ?? defaultGroupTemplate\"\n          [ngTemplateOutletContext]=\"{ $implicit: item }\"\n        ></ng-container>\n      </span>\n      <ng-container\n        *ngFor=\"let optionRow of item.options; trackBy: trackByItem\"\n        [ngTemplateOutlet]=\"optionRowTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: optionRow }\"\n      ></ng-container>\n    </div>\n    <ng-container\n      *ngIf=\"item.type === 'option'\"\n      [ngTemplateOutlet]=\"optionRowTemplate\"\n      [ngTemplateOutletContext]=\"{ $implicit: item }\"\n    ></ng-container>\n  </ng-container>\n  <ng-template siSelectOptionRowTemplate let-option #optionRowTemplate>\n    <si-select-option-row\n      #cdkOption=\"cdkOption\"\n      class=\"dropdown-item focus-inside pe-4\"\n      [option]=\"option\"\n      [optionTemplate]=\"optionTemplate\"\n      [cdkOption]=\"option.value\"\n      [cdkOptionDisabled]=\"option.disabled\"\n      [cdkOptionTypeaheadLabel]=\"option.typeaheadLabel! | translate\"\n      [class.active]=\"cdkOption.isActive()\"\n      [selected]=\"cdkOption.isSelected()\"\n    ></si-select-option-row>\n  </ng-template>\n</div>\n\n<ng-template siSelectGroupTemplate let-group #defaultGroupTemplate>\n  {{ group.label | translate }}\n</ng-template>\n" }]
        }], propDecorators: { listbox: [{
                type: ViewChild,
                args: [CdkListbox, { read: ElementRef }]
            }] } });

class SiSelectComponent {
    static idCounter = 0;
    /**
     * Unique identifier.
     */
    id = `__si-select-${SiSelectComponent.idCounter++}`;
    /** Aria label of the select. */
    ariaLabel = null;
    /** Aria labelledby of the select. */
    labelledby = null;
    filterPlaceholder = $localize `:@@SI_SELECT.SEARCH-PLACEHOLDER:Search...`;
    noResultsFoundLabel = $localize `:@@SI_SELECT.NO-RESULTS-FOUND:No results found`;
    /** Emits on selection dropdown close. */
    dropdownClose = new EventEmitter();
    open = false;
    optionTemplate;
    groupTemplate;
    trigger;
    rows = [];
    overlayWidth = 0;
    selectionStrategy = inject((SiSelectSelectionStrategy));
    backdropClicked = false;
    selectOptions = inject(SI_SELECT_OPTIONS_STRATEGY);
    _readonly = false;
    get readonly() {
        return this._readonly;
    }
    /**
     * Readonly state. Similar to disabled but with higher contrast
     * @defaultref {@link _readonly}
     **/
    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
    }
    _hasFilter = false;
    get hasFilter() {
        return this._hasFilter;
    }
    /**
     * Enables the filter input
     *
     * @defaultref {@link SiSelectComponent#_hasFilter}
     */
    set hasFilter(value) {
        this._hasFilter = coerceBooleanProperty(value);
        if (this._hasFilter) {
            this.verifyValueProvider();
        }
    }
    trackByItem = (index, item) => item;
    ngAfterContentInit() {
        this.verifyValueProvider();
    }
    openListBox() {
        if (this._readonly || this.selectionStrategy.disabled) {
            return;
        }
        this.overlayWidth = this.trigger.nativeElement.getBoundingClientRect().width + 2; // 2px border
        this.open = true;
    }
    overlayDetach() {
        this.open = false;
        if (!this.backdropClicked) {
            this.trigger.nativeElement.focus();
        }
        else {
            this.backdropClicked = false;
            this.selectionStrategy.onTouched();
        }
        this.dropdownClose.emit();
    }
    backdropClick() {
        this.backdropClicked = true;
        this.open = false;
    }
    verifyValueProvider() {
        if (this.hasFilter &&
            this.optionTemplate &&
            this.selectOptions instanceof SiSelectComplexOptionsDirective &&
            !this.selectOptions.valueProvider) {
            console.error('A valueProvider is required when [hasFilter]="true" and having custom option template on si-select');
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectComponent, isStandalone: true, selector: "si-select", inputs: { id: "id", ariaLabel: "ariaLabel", labelledby: "labelledby", filterPlaceholder: "filterPlaceholder", noResultsFoundLabel: "noResultsFoundLabel", readonly: "readonly", hasFilter: "hasFilter" }, outputs: { dropdownClose: "dropdownClose" }, host: { properties: { "class.open": "this.open", "class.readonly": "this.readonly", "class.si-select-has-filter": "this.hasFilter" }, classAttribute: "dropdown" }, queries: [{ propertyName: "optionTemplate", first: true, predicate: SiSelectOptionTemplateDirective, descendants: true, read: TemplateRef }, { propertyName: "groupTemplate", first: true, predicate: SiSelectGroupTemplateDirective, descendants: true, read: TemplateRef }], viewQueries: [{ propertyName: "trigger", first: true, predicate: CdkOverlayOrigin, descendants: true, read: ElementRef, static: true }], ngImport: i0, template: "<si-select-input\n  #trigger=\"cdkOverlayOrigin\"\n  cdkOverlayOrigin\n  [baseId]=\"id\"\n  [labelledby]=\"labelledby\"\n  [optionTemplate]=\"optionTemplate\"\n  [ariaLabel]=\"ariaLabel\"\n  [controls]=\"id + '-listbox'\"\n  [open]=\"open\"\n  [readonly]=\"readonly\"\n  (openListbox)=\"openListBox()\"\n></si-select-input>\n\n<ng-template\n  cdkConnectedOverlay\n  cdkConnectedOverlayBackdropClass=\"cdk-overlay-transparent-backdrop\"\n  [cdkConnectedOverlayHasBackdrop]=\"true\"\n  [cdkConnectedOverlayOrigin]=\"trigger\"\n  [cdkConnectedOverlayOpen]=\"open\"\n  [cdkConnectedOverlayFlexibleDimensions]=\"true\"\n  [cdkConnectedOverlayOffsetX]=\"-1\"\n  [cdkConnectedOverlayWidth]=\"overlayWidth\"\n  (backdropClick)=\"backdropClick()\"\n  (detach)=\"overlayDetach()\"\n>\n  <si-select-list\n    *ngIf=\"!hasFilter; else hasFilterTemplate\"\n    [baseId]=\"id\"\n    [optionTemplate]=\"optionTemplate\"\n    [groupTemplate]=\"groupTemplate\"\n    [labelledby]=\"labelledby\"\n    (closeOverlay)=\"open = false\"\n  ></si-select-list>\n\n  <ng-template #hasFilterTemplate>\n    <si-select-list-has-filter\n      [baseId]=\"id\"\n      [filterPlaceholder]=\"filterPlaceholder\"\n      [noResultsFoundLabel]=\"noResultsFoundLabel\"\n      [optionTemplate]=\"optionTemplate\"\n      [groupTemplate]=\"groupTemplate\"\n      [labelledby]=\"labelledby\"\n      (closeOverlay)=\"overlayDetach()\"\n    ></si-select-list-has-filter>\n  </ng-template>\n</ng-template>\n", styles: [":host{display:inline-block;padding:0;block-size:2rem}:host:focus-within,:host.open:not(.si-select-has-filter){box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}:host.readonly,:host.disabled{pointer-events:none}.dropdown-menu{max-block-size:min(100vh,266px)}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: OverlayModule }, { kind: "directive", type: i2$2.CdkConnectedOverlay, selector: "[cdk-connected-overlay], [connected-overlay], [cdkConnectedOverlay]", inputs: ["cdkConnectedOverlayOrigin", "cdkConnectedOverlayPositions", "cdkConnectedOverlayPositionStrategy", "cdkConnectedOverlayOffsetX", "cdkConnectedOverlayOffsetY", "cdkConnectedOverlayWidth", "cdkConnectedOverlayHeight", "cdkConnectedOverlayMinWidth", "cdkConnectedOverlayMinHeight", "cdkConnectedOverlayBackdropClass", "cdkConnectedOverlayPanelClass", "cdkConnectedOverlayViewportMargin", "cdkConnectedOverlayScrollStrategy", "cdkConnectedOverlayOpen", "cdkConnectedOverlayDisableClose", "cdkConnectedOverlayTransformOriginOn", "cdkConnectedOverlayHasBackdrop", "cdkConnectedOverlayLockPosition", "cdkConnectedOverlayFlexibleDimensions", "cdkConnectedOverlayGrowAfterOpen", "cdkConnectedOverlayPush"], outputs: ["backdropClick", "positionChange", "attach", "detach", "overlayKeydown", "overlayOutsideClick"], exportAs: ["cdkConnectedOverlay"] }, { kind: "directive", type: i2$2.CdkOverlayOrigin, selector: "[cdk-overlay-origin], [overlay-origin], [cdkOverlayOrigin]", exportAs: ["cdkOverlayOrigin"] }, { kind: "component", type: SiSelectInputComponent, selector: "si-select-input", inputs: ["baseId", "labelledby", "ariaLabel", "open", "controls", "optionTemplate", "readonly"], outputs: ["openListbox"] }, { kind: "component", type: SiSelectListComponent, selector: "si-select-list" }, { kind: "component", type: SiSelectListHasFilterComponent, selector: "si-select-list-has-filter", inputs: ["filterPlaceholder", "noResultsFoundLabel"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-select', changeDetection: ChangeDetectionStrategy.OnPush, imports: [
                        CommonModule,
                        OverlayModule,
                        SiSelectInputComponent,
                        SiSelectListComponent,
                        SiSelectListHasFilterComponent
                    ], standalone: true, host: {
                        class: 'dropdown'
                    }, template: "<si-select-input\n  #trigger=\"cdkOverlayOrigin\"\n  cdkOverlayOrigin\n  [baseId]=\"id\"\n  [labelledby]=\"labelledby\"\n  [optionTemplate]=\"optionTemplate\"\n  [ariaLabel]=\"ariaLabel\"\n  [controls]=\"id + '-listbox'\"\n  [open]=\"open\"\n  [readonly]=\"readonly\"\n  (openListbox)=\"openListBox()\"\n></si-select-input>\n\n<ng-template\n  cdkConnectedOverlay\n  cdkConnectedOverlayBackdropClass=\"cdk-overlay-transparent-backdrop\"\n  [cdkConnectedOverlayHasBackdrop]=\"true\"\n  [cdkConnectedOverlayOrigin]=\"trigger\"\n  [cdkConnectedOverlayOpen]=\"open\"\n  [cdkConnectedOverlayFlexibleDimensions]=\"true\"\n  [cdkConnectedOverlayOffsetX]=\"-1\"\n  [cdkConnectedOverlayWidth]=\"overlayWidth\"\n  (backdropClick)=\"backdropClick()\"\n  (detach)=\"overlayDetach()\"\n>\n  <si-select-list\n    *ngIf=\"!hasFilter; else hasFilterTemplate\"\n    [baseId]=\"id\"\n    [optionTemplate]=\"optionTemplate\"\n    [groupTemplate]=\"groupTemplate\"\n    [labelledby]=\"labelledby\"\n    (closeOverlay)=\"open = false\"\n  ></si-select-list>\n\n  <ng-template #hasFilterTemplate>\n    <si-select-list-has-filter\n      [baseId]=\"id\"\n      [filterPlaceholder]=\"filterPlaceholder\"\n      [noResultsFoundLabel]=\"noResultsFoundLabel\"\n      [optionTemplate]=\"optionTemplate\"\n      [groupTemplate]=\"groupTemplate\"\n      [labelledby]=\"labelledby\"\n      (closeOverlay)=\"overlayDetach()\"\n    ></si-select-list-has-filter>\n  </ng-template>\n</ng-template>\n", styles: [":host{display:inline-block;padding:0;block-size:2rem}:host:focus-within,:host.open:not(.si-select-has-filter){box-shadow:0 0 0 var(--element-button-focus-overlay-width) var(--element-button-focus-overlay-color),0 0 0 calc(var(--element-button-focus-width) + var(--element-button-focus-overlay-width)) var(--element-focus-default)}:host.readonly,:host.disabled{pointer-events:none}.dropdown-menu{max-block-size:min(100vh,266px)}\n"] }]
        }], propDecorators: { id: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], labelledby: [{
                type: Input
            }], filterPlaceholder: [{
                type: Input
            }], noResultsFoundLabel: [{
                type: Input
            }], dropdownClose: [{
                type: Output
            }], open: [{
                type: HostBinding,
                args: ['class.open']
            }], optionTemplate: [{
                type: ContentChild,
                args: [SiSelectOptionTemplateDirective, { read: TemplateRef }]
            }], groupTemplate: [{
                type: ContentChild,
                args: [SiSelectGroupTemplateDirective, { read: TemplateRef }]
            }], trigger: [{
                type: ViewChild,
                args: [CdkOverlayOrigin, { read: ElementRef, static: true }]
            }], readonly: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.readonly']
            }], hasFilter: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.si-select-has-filter']
            }] } });

/**
 * The directive allows to pass SelectOption options.
 * Otherwise use the {@link SiSelectComplexOptionsDirective} directive.
 *
 * @example
 * <si-select [options]="[{ id: 'one', title: 'One' }, { id: 'two', title: 'Two' }]"></si-select>
 */
class SiSelectSimpleOptionsDirective extends SiSelectOptionsStrategyBase {
    /** @internal */
    trackBy = buildTrackByIdentity();
    /** Options to be shown in select dropdown */
    options;
    /** @internal */
    optionsEqual = (a, b) => a === b;
    ngOnChanges() {
        this.allRowsSubject$.next(this.options.map(option => ({
            type: 'option',
            value: option.id,
            label: option.title,
            color: option.color,
            icon: option.icon,
            disabled: option.disabled
        })));
        this.updateOptionsSubject();
    }
    onFilter(filterValue) {
        this.updateOptionsSubject(filterValue);
    }
    updateOptionsSubject(filterValue) {
        if (filterValue) {
            const filterValueLC = filterValue.toLowerCase();
            this.rowsSubject$.next(this.allRows.filter(row => row.label.toLowerCase().includes(filterValueLC)));
        }
        else {
            this.rowsSubject$.next(this.allRows);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectSimpleOptionsDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectSimpleOptionsDirective, isStandalone: true, selector: "si-select[options]", inputs: { options: "options" }, providers: [{ provide: SI_SELECT_OPTIONS_STRATEGY, useExisting: SiSelectSimpleOptionsDirective }], usesInheritance: true, usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectSimpleOptionsDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'si-select[options]',
                    providers: [{ provide: SI_SELECT_OPTIONS_STRATEGY, useExisting: SiSelectSimpleOptionsDirective }],
                    standalone: true
                }]
        }], propDecorators: { options: [{
                type: Input
            }] } });

/**
 * The directive enables the single-select behavior.
 * Otherwise use the {@link SiSelectMultiValueDirective} directive.
 *
 * @example
 * <si-select [(value)]="selectedValue" [options]="[
 *  { id: 'good', title: 'Good' },
 *  { id: 'average', title: 'Average' },
 *  { id: 'poor', title: 'Poor' }
 * ]"></si-select>
 */
class SiSelectSingleValueDirective extends SiSelectSelectionStrategy {
    allowMultiple = false;
    toArrayValue(value) {
        return value ? [value] : [];
    }
    fromArrayValue([value]) {
        return value;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectSingleValueDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectSingleValueDirective, isStandalone: true, selector: "si-select:not([multi])", providers: [
            { provide: SiSelectSelectionStrategy, useExisting: SiSelectSingleValueDirective },
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: SiSelectSingleValueDirective,
                multi: true
            }
        ], usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectSingleValueDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'si-select:not([multi])',
                    providers: [
                        { provide: SiSelectSelectionStrategy, useExisting: SiSelectSingleValueDirective },
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SiSelectSingleValueDirective,
                            multi: true
                        }
                    ],
                    standalone: true
                }]
        }] });

/**
 * The directive enables the multi-select behavior.
 * Otherwise use the {@link SiSelectSingleValueDirective} directive.
 *
 * @example
 * <si-select multi [(value)]="multiValue" [options]="[
 *  { id: 'good', title: 'Good' },
 *  { id: 'average', title: 'Average' },
 *  { id: 'poor', title: 'Poor' }
 * ]"></si-select>
 */
class SiSelectMultiValueDirective extends SiSelectSelectionStrategy {
    allowMultiple = true;
    fromArrayValue(value) {
        return value;
    }
    toArrayValue(value) {
        return value ?? [];
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectMultiValueDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiSelectMultiValueDirective, isStandalone: true, selector: "si-select[multi]", providers: [
            { provide: SiSelectSelectionStrategy, useExisting: SiSelectMultiValueDirective },
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: SiSelectMultiValueDirective,
                multi: true
            }
        ], usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectMultiValueDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'si-select[multi]',
                    providers: [
                        { provide: SiSelectSelectionStrategy, useExisting: SiSelectMultiValueDirective },
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SiSelectMultiValueDirective,
                            multi: true
                        }
                    ],
                    standalone: true
                }]
        }] });

class SiSelectModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiSelectModule, imports: [SiSelectComplexOptionsDirective,
            SiSelectComponent,
            SiSelectGroupTemplateDirective,
            SiSelectMultiValueDirective,
            SiSelectOptionTemplateDirective,
            SiSelectSimpleOptionsDirective,
            SiSelectSingleValueDirective], exports: [SiSelectComplexOptionsDirective,
            SiSelectComponent,
            SiSelectGroupTemplateDirective,
            SiSelectMultiValueDirective,
            SiSelectOptionTemplateDirective,
            SiSelectSimpleOptionsDirective,
            SiSelectSingleValueDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectModule, imports: [SiSelectComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        SiSelectComplexOptionsDirective,
                        SiSelectComponent,
                        SiSelectGroupTemplateDirective,
                        SiSelectMultiValueDirective,
                        SiSelectOptionTemplateDirective,
                        SiSelectSimpleOptionsDirective,
                        SiSelectSingleValueDirective
                    ],
                    exports: [
                        SiSelectComplexOptionsDirective,
                        SiSelectComponent,
                        SiSelectGroupTemplateDirective,
                        SiSelectMultiValueDirective,
                        SiSelectOptionTemplateDirective,
                        SiSelectSimpleOptionsDirective,
                        SiSelectSingleValueDirective
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiSelectComplexOptionsDirective, SiSelectComponent, SiSelectGroupTemplateDirective, SiSelectModule, SiSelectMultiValueDirective, SiSelectOptionTemplateDirective, SiSelectSelectionStrategy, SiSelectSimpleOptionsDirective, SiSelectSingleValueDirective };
